import * as React from "react"
import { ConnectionStatusType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import Cookies from "js-cookie"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { addConnectionStatus } from "@/lib/api/connection-status"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Header } from "@/components/header"

import { Icons } from "../icons"
import { Card } from "../ui/card"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"

const formSchema = z.object({
  sequence: z
    .string()
    .min(1, "Sequence is required.")
    .refine((val) => !isNaN(Number(val)) && Number(val) >= 1, {
      message: "Sequence must be a valid number greater than or equal to 1",
    })
    .transform((val) => Number(val)),

  status_name: z.string().min(1, "Status name  is required"),
  description: z.string().min(1, "Description is required"),
  icon: z.string().min(1, "Icon is required"),
})

export default function ConnectionStatusForm({
  setShowAddConnectionStatusModal,
}: {
  setShowAddConnectionStatusModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      //sequence: 0,
      status_name: "",
      description: "",
      icon: "",
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: ConnectionStatusType) => {
      await addConnectionStatus(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectionStatusDetails"] })
      toast({
        description: "New Connection Status added successfully.",
      })
      setShowAddConnectionStatusModal(false)
    },
    onError: async (error: any) => {
      const response = error.response
      let errorMessage = "Please try again."
      console.log(error)

      if (response && response.status === 400) {
        const errorData = await response.json() // Read the response body
        console.error("API Error Response:", errorData)
        errorMessage = errorData.message || JSON.stringify(errorData)
      }

      toast({
        title: "Something went wrong!",
        description: errorMessage,
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    //console.log("Submitting values:", values)
    mutation.mutate(values as ConnectionStatusType)
  }

  return (
    <div className="px-2">
      <Header
        subroutes={[
          {
            route: "/meter-connection-settings/connection-status",
            label: "Connection Status",
          },
        ]}
      >
        Add Connection Status
      </Header>
      <DropdownMenuSeparator className="mt-4" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
        >
          <Card className="my-4 gap-4 p-6">
            <FormField
              control={form.control}
              name="status_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Status Name<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Description<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sequence"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Sequence<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="icon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Icon<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
          <div className="flex flex-row justify-end gap-2 md:mr-4">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowAddConnectionStatusModal(false)
              }}
              className="w-28"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="w-28"
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Add
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
